import React, { useState, useEffect, useRef } from 'react';
import { Card, Alert, Container, Row, Col, Button, Form } from 'react-bootstrap';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { confirmEmail, requestConfirmEmail } from '../../services/auth';
import { useAuth } from '../../contexts/AuthContext';

const EmailConfirmation = () => {
  const navigate = useNavigate();
  const { code, email } = useParams();
  const { refreshUser, isAuthenticated } = useAuth();
  const [status, setStatus] = useState('processing'); // 'processing', 'success', 'error'
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [emailInput, setEmailInput] = useState('');
  const [resendStatus, setResendStatus] = useState(''); // '', 'success', 'error'
  const [resendMessage, setResendMessage] = useState('');
  const confirmationAttempted = useRef(false);

  useEffect(() => {
    // Only attempt to confirm email once to prevent infinite loops
    if (confirmationAttempted.current) {
      return;
    }

    const confirmUserEmail = async () => {
      confirmationAttempted.current = true;

      try {
        // If email is available, set it in the input field for convenience
        if (email) {
          setEmailInput(email);
        }

        // Validate parameters
        if (!code || !email) {
          setStatus('error');
          setMessage('Missing confirmation parameters. Please check your email link and try again.');
          setIsLoading(false);
          return;
        }

        // Decode the email parameter (since it might be URL-encoded)
        const decodedEmail = decodeURIComponent(email);

        // Call the API to confirm email
        console.log('Confirming email:', decodedEmail, 'with code:', code);
        const response = await confirmEmail(decodedEmail, code);
        console.log('Email confirmation response:', response);

        // Update status based on response
        setStatus('success');
        setMessage('Your email has been successfully confirmed! You can now use all features of your account.');

        // Only refresh user data if the user is already authenticated
        // This prevents issues when confirming email while not logged in
        if (isAuthenticated) {
          try {
            console.log('User is authenticated, refreshing user data');
            await refreshUser();
          } catch (refreshError) {
            console.error('Error refreshing user data:', refreshError);
            // Don't change the success status if refresh fails
          }
        } else {
          console.log('User is not authenticated, skipping refresh');
        }
      } catch (error) {
        console.error('Email confirmation error:', error);
        setStatus('error');

        if (error.errors && error.errors.token) {
          setMessage('Invalid or expired confirmation code. Please request a new confirmation email.');
        } else {
          setMessage('An error occurred while confirming your email. Please try again later.');
        }
      } finally {
        setIsLoading(false);
      }
    };

    confirmUserEmail();
  }, [code, email, refreshUser, isAuthenticated]);

  return (
    <Container>
      <Row className="justify-content-center">
        <Col md={8} lg={6}>
          <Card className="shadow-sm border-0 my-5">
            <Card.Body className="p-4">
              <div className="text-center mb-4">
                <h2 className="fw-bold">Email Confirmation</h2>
              </div>

              {isLoading ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-3">Verifying your email...</p>
                </div>
              ) : (
                <>
                  <Alert variant={status === 'success' ? 'success' : 'danger'}>
                    {message}
                  </Alert>

                  <div className="d-grid gap-2 mt-4">
                    {status === 'success' ? (
                      <Button as={Link} to="/" variant="primary" size="lg">
                        Go to Homepage
                      </Button>
                    ) : (
                      <>
                        <Button as={Link} to="/" variant="primary" size="lg">
                          Go to Homepage
                        </Button>
                        <Button as={Link} to="/login" variant="outline-primary" size="lg">
                          Go to Login
                        </Button>

                        {/* Resend confirmation email section */}
                        <div className="mt-4 pt-3 border-top">
                          <h5 className="mb-3">Need a new confirmation email?</h5>

                          {resendStatus && (
                            <Alert variant={resendStatus === 'success' ? 'success' : 'danger'} className="mb-3">
                              {resendMessage}
                            </Alert>
                          )}

                          <Form onSubmit={async (e) => {
                            e.preventDefault();
                            if (!emailInput) return;

                            try {
                              setResendStatus('');
                              setResendMessage('');

                              await requestConfirmEmail(emailInput);
                              setResendStatus('success');
                              setResendMessage('A new confirmation email has been sent. Please check your inbox.');
                            } catch (error) {
                              console.error('Error requesting confirmation email:', error);
                              setResendStatus('error');

                              if (error.confirmed) {
                                setResendMessage('This email is already confirmed. Please try logging in.');
                              } else {
                                setResendMessage('Failed to send confirmation email. Please check if the email is correct.');
                              }
                            }
                          }}>
                            <Form.Group className="mb-3">
                              <Form.Label>Your Email Address</Form.Label>
                              <Form.Control
                                type="email"
                                value={emailInput}
                                onChange={(e) => setEmailInput(e.target.value)}
                                placeholder="Enter your email"
                                required
                              />
                            </Form.Group>
                            <Button type="submit" variant="outline-primary" className="w-100">
                              Resend Confirmation Email
                            </Button>
                          </Form>
                        </div>
                      </>
                    )}
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default EmailConfirmation;
